import { useState } from "react";
import { createUserWithEmailAndPassword, deleteUser } from "firebase/auth";
import { auth } from "../firebase";
import { Link, useNavigate } from "react-router-dom";
import { registerProfile } from "../lib/profileStore";

const logoUrl = process.env.PUBLIC_URL + "/new_logo.png";

function normalizePhone(raw) {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return "1" + digits;
  return digits;
}

export default function Signup() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const normalized = normalizePhone(phone);
    if (normalized.length < 10) {
      setError("Please enter a valid phone number.");
      setLoading(false);
      return;
    }

    let createdUser = null;
    try {
      // Authenticate before touching the private phone trust index. The
      // transaction below is the uniqueness check, so concurrent signups
      // cannot both claim the same number.
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      createdUser = cred.user;
      const { uid } = cred.user;

      await registerProfile(uid, normalized, {
        uid,
        full_name: fullName.trim(),
        email: email.trim(),
        school: "",
        industry: "",
        role: "",
        organization: "",
        profile_photo_url: "",
        location: "",
        privacy_settings: {
          show_email: false,
          show_school: true,
          show_industry: true,
          show_role: true,
          show_organization: true,
        },
      });

      navigate("/onboarding");
    } catch (err) {
      if (createdUser) {
        try {
          await deleteUser(createdUser);
        } catch (_) {
          // Preserve the registration error; a stale Auth-only account can be
          // retried with the normal account-recovery flow.
        }
      }
      const msgs = {
        "auth/email-already-in-use": "An account with this email already exists.",
        "auth/invalid-email": "Please enter a valid email.",
        "auth/weak-password": "Password must be at least 6 characters.",
        "profile/phone-already-in-use": "An account with this phone number already exists.",
      };
      setError(msgs[err.code] || err.message || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">
          <img src={logoUrl} alt="Aline" className="logo" />
        </div>
        <h2>Create your rolodex</h2>
        <p className="auth-subtitle">Private. Personal. Yours.</p>

        {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-field">
            <label>Full name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Smith"
              required
              autoFocus
            />
          </div>
          <div className="form-field">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>
          <div className="form-field">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              required
            />
          </div>
          <div className="form-field">
            <label>Phone number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 000-0000"
              required
            />
            <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
              This is your trust key — used to verify real-life connections.
            </span>
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="auth-switch">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
